import { memo, useMemo } from 'react';
import { buildLinePath, downsampleRuns, splitOnGaps, type ChartPoint } from '../../utils/chartMath';

interface SparklineProps {
  points: ChartPoint[];
  width?: number;
  height?: number;
  color?: string;
  className?: string;
  /** Described by the surrounding row, so the graphic itself is decorative by default. */
  ariaLabel?: string;
}

const MAX_POINTS = 60;

/**
 * Inline micro-chart for table rows.
 *
 * No axes, no interaction, no tooltip: it exists to show shape at a glance beside the numbers
 * that carry the detail. Scaled to its own min and max, so a quiet server and a busy one both
 * fill the same box — comparison happens through the adjacent columns, not the sparkline.
 */
export const Sparkline = memo(
  ({ points, width = 88, height = 24, color = 'var(--accent-300)', className, ariaLabel }: SparklineProps) => {
    const path = useMemo(() => {
      const runs = downsampleRuns(splitOnGaps(points), MAX_POINTS, 'minmax');
      const values = runs.flat().map((p) => p.v);
      if (values.length < 2) return '';

      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = max - min || 1;
      const times = runs.flat().map((p) => p.t);
      const tMin = Math.min(...times);
      const tSpan = Math.max(...times) - tMin || 1;

      // One unit of padding top and bottom keeps a flat line off the very edge.
      return buildLinePath(
        runs.map((run) =>
          run.map((p) => ({
            x: ((p.t - tMin) / tSpan) * width,
            y: height - 1 - ((p.v - min) / span) * (height - 2),
          })),
        ),
      );
    }, [points, width, height]);

    if (!path) {
      return (
        <span
          className={className}
          style={{ display: 'inline-block', width, height }}
          aria-hidden="true"
        />
      );
    }

    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className={className}
        role={ariaLabel ? 'img' : 'presentation'}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true}
      >
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  },
);

Sparkline.displayName = 'Sparkline';
